// popup.service.ts
import {Injectable, Injector, Type, ComponentRef} from '@angular/core';
import {Overlay, OverlayRef} from "@angular/cdk/overlay";
import {ComponentPortal} from "@angular/cdk/portal";
import {PopupHostComponent} from "../components/popup-host/popup-host.component";
import {Subject, takeUntil} from "rxjs";

export interface PopupOpenOptions<T> {
    x: number;
    y: number;
    inputs?: Partial<T>;
    hasBackdrop?: boolean;
    backdropClass?: string;
    panelClass?: string | string[];
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    draggable?: boolean;
    dragRootSelector?: string;
}

export interface PopupHandle<T> {
    overlayRef: OverlayRef;
    componentRef: ComponentRef<T>;
    close: () => void;
}

@Injectable({providedIn: 'root'})
export class PopupService {
    constructor(private overlay: Overlay, private injector: Injector) {
    }

    open<T>(
        component: Type<T>,
        opts: PopupOpenOptions<T>,
    ): PopupHandle<T> {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo({x: opts.x, y: opts.y})
            .withPositions([
                {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top'},
                {originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'top'},
                {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom'},
                {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'bottom'},
            ])
            .withPush(true); // keep it inside viewport

        const overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            hasBackdrop: opts.hasBackdrop ?? true,
            backdropClass: opts.backdropClass ?? 'cdk-overlay-transparent-backdrop',
            panelClass: opts.panelClass ?? 'wire-popup-panel',
        });

        // Attach a host, then attach your real component into that host VC
        const hostPortal = new ComponentPortal(PopupHostComponent, null, this.injector);
        const hostRef = overlayRef.attach(hostPortal);

        hostRef.setInput('draggable', opts.draggable ?? false);
        if (opts.dragRootSelector) hostRef.setInput('dragRootSelector', opts.dragRootSelector);

        const componentRef = hostRef.instance.vc.createComponent(component, {
            injector: this.injector,
        });

        // Set inputs safely (Angular 16+ style)
        if (opts.inputs) {
            for (const [k, v] of Object.entries(opts.inputs)) {
                componentRef.setInput(k, v as any);
            }
        }

        const disposed$ = new Subject<void>();
        const close = () => {
            disposed$.next();
            disposed$.complete();
            overlayRef.dispose();
        };

        if (opts.closeOnBackdrop ?? true) {
            overlayRef.backdropClick().pipe(takeUntil(disposed$)).subscribe(() => close());
        }
        if (opts.closeOnEscape ?? true) {
            overlayRef.keydownEvents().pipe(takeUntil(disposed$)).subscribe((ev) => {
                if (ev.key === 'Escape') close();
            });
        }

        return {overlayRef, componentRef, close};
    }
}
